import { useState, useEffect } from "react";
import CustomButton from "../../Buttons/CustomButton";
import { showToast } from "../../Buttons/Toaster";

const GitHubIntegration = ({ initialConfig, onConfigChange, isLoading }) => {
  const [githubConfig, setGithubConfig] = useState({
    username: "",
    repository: "",
    accessToken: "",
    expiryDate: "",
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  // const [isLoading, setIsLoading] = useState(true);

  // Load GitHub configuration from backend on component mount
  useEffect(() => {
    // Populate form with data passed down from Dashboard
    if (initialConfig) {
      setGithubConfig({
        username: initialConfig.username || "",
        repository: initialConfig.repo_name || "",
        accessToken: initialConfig.access_token || "",
        expiryDate: initialConfig.expiry_date || "",
      });
    }
  }, [initialConfig]); // Re-run if the initialConfig from props changes

  // Handle input changes
  const handleGithubConfigChange = (field, value) => {
    setGithubConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

 const saveGithubConfig = async () => {
  // Validate required fields
  if (
    !githubConfig.username ||
    !githubConfig.repository ||
    !githubConfig.accessToken
  ) {
    showToast("Please fill in all required fields", "error");
    return false;
  }

  try {
    const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;
    const access_token = sessionStorage.getItem("access_token");
    const user_id = sessionStorage.getItem("user_id");

    if (!user_id) {
      throw new Error("User ID not found in session storage");
    }

    // Create URL-encoded form data
    const formData = new URLSearchParams();
    formData.append("username", githubConfig.username);
    formData.append("repo_name", githubConfig.repository);
    formData.append("access_token", githubConfig.accessToken);
    formData.append("expiry_date", githubConfig.expiryDate);
    formData.append("user_id", user_id);

    const response = await fetch(`${apiBase}/config/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${access_token}`,
      },
      body: formData.toString(),
    });

    const result = await response.json();

    if (response.ok && result.status === "SUCCESS") {
      // Save to URL parameters
      const configString = JSON.stringify(githubConfig);
      const encodedConfig = encodeURIComponent(configString);

      // Update URL without reloading the page
      const url = new URL(window.location);
      url.searchParams.set("githubConfig", encodedConfig);
      window.history.replaceState({}, "", url);

      showToast("GitHub configuration saved successfully!", "success");
      
      // FIX: Create a complete config object with all fields properly mapped
      const updatedConfig = {
        username: githubConfig.username,
        repo_name: githubConfig.repository,
        access_token: githubConfig.accessToken,
        expiry_date: githubConfig.expiryDate,
        // Include any other fields that might come from the backend
        ...(initialConfig || {}) // Preserve other existing properties
      };

      // Call the function passed from Dashboard to update the global state
     onConfigChange({
          isConfigured: true,
          details: {
            ...initialConfig,
            username: githubConfig.username,
            repo_name: githubConfig.repository,
            access_token: githubConfig.accessToken, // Mapped to snake_case
            expiry_date: githubConfig.expiryDate,   // Mapped to snake_case
          },
        });

      return true;
    } else {
      throw new Error(
        result.detail || result.message || "Failed to save configuration"
      );
    }
  } catch (error) {
    console.error("Error saving GitHub config:", error);
    showToast(error.message || "Error saving GitHub configuration", "error");
    onConfigChange({ isConfigured: false, details: initialConfig });
    return false;
  }
};

  const testGithubConnection = async () => {
    // Validate required fields
    if (
      !githubConfig.username ||
      !githubConfig.repository ||
      !githubConfig.accessToken
    ) {
      showToast(
        "Please fill in all required fields to test connection",
        "error"
      );
      return false;
    }

    setConnectionStatus(null);

    try {
      const apiBase = process.env.REACT_APP_API_URL_CODE_CONVERSION;
      const access_token = sessionStorage.getItem("access_token");
      const user_id = sessionStorage.getItem("user_id");

      // Create URL-encoded form data
      const formData = new URLSearchParams();
      formData.append("username", githubConfig.username);
      formData.append("repo_name", githubConfig.repository);
      formData.append("access_token", githubConfig.accessToken);
      formData.append("expiry_date", githubConfig.expiryDate);
      formData.append("user_id", user_id);

      const response = await fetch(`${apiBase}/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${access_token}`,
        },
        body: formData.toString(),
      });

      const result = await response.json();

      if (response.ok && result.status === "SUCCESS") {
        setConnectionStatus("success");
        showToast("Connected successfully!", "success");
        return true;
      } else {
        setConnectionStatus("error");
        showToast(
          result.error || result.detail || "Connection failed",
          "error"
        );
        return false;
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setConnectionStatus("error");
      showToast("Connection test failed", "error");
      return false;
    }
  };

  const handleTestAndSave = async () => {
    setIsProcessing(true);

    try {
      // First test the connection
      const testResult = await testGithubConnection();

      // Add a small delay to ensure the test toaster is visible
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (testResult) {
        // If test is successful, save the configuration
        const saveResult = await saveGithubConfig();

        if (saveResult) {
          setConnectionStatus("saved");
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="border ">
        <h5 className="p-3 mb-0 border-bottom">GitHub Integration</h5>
        <div className="p-3 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading GitHub configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border userfixed">
      <h5 className="p-3 mb-0 border-bottom">GitHub Integration</h5>
      <div className="p-3">
        <p className="text-muted mb-4">
          Configure your GitHub connection to push converted code directly.
        </p>

        <div className="row mb-3">
          <label className="col-sm-2 col-form-label d-flex align-items-center">
            <span style={{ whiteSpace: "nowrap" }}>
              GitHub Username <span style={{ color: "red" }}>*</span>
            </span>
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control highlighted-input colorfor-placeholder styled-input"
              placeholder="Enter your username"
              value={githubConfig.username}
              onChange={(e) =>
                handleGithubConfigChange("username", e.target.value)
              }
            />
          </div>
        </div>

       <div className="row mb-3">
  <label className="col-sm-2 col-form-label d-flex align-items-center">
    <span style={{ whiteSpace: "nowrap" }}>
      Repository Name <span style={{ color: "red" }}>*</span>
    </span>
  </label>
  <div className="col-sm-10">
    <input
      type="text"
      className="form-control highlighted-input colorfor-placeholder styled-input"
      placeholder="Enter your repository name"
      value={githubConfig.repository}
      onChange={(e) =>
        handleGithubConfigChange("repository", e.target.value)
      }
    />
  </div>
</div>


        <div className="row mb-3">
          <label className="col-sm-2 col-form-label">
            Access Token <span style={{ color: "red" }}>*</span>
          </label>
          <div className="col-sm-10">
            <input
              type="password"
              className="form-control highlighted-input colorfor-placeholder styled-input form-control"
              value={githubConfig.accessToken}
              onChange={(e) =>
                handleGithubConfigChange("accessToken", e.target.value)
              }
              placeholder="Enter your GitHub access token"
            />
          </div>
        </div>

        <div className="row mb-4">
          <label className="col-sm-2 col-form-label">
            Expiry Date <span style={{ color: "red" }}>*</span>
          </label>
          <div className="col-sm-10">
            <input
              type="date"
              className="form-control highlighted-input colorfor-placeholder styled-input form-control"
              value={githubConfig.expiryDate}
              onChange={(e) =>
                handleGithubConfigChange("expiryDate", e.target.value)
              }
            />
          </div>
        </div>

        <div className="row">
          <div className="col-sm-10 offset-sm-2">
            <div className="d-flex gap-2">
              <CustomButton
                text={
                  isProcessing ? "Processing..." : "Test & Save Configuration"
                }
                onClick={handleTestAndSave}
                isEditEnabled={true}
                isDisabled={isProcessing}
              />
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-sm-10 offset-sm-2">
            <div className="alert alert-info mb-0" role="alert">
              <small>
                <i className="fas fa-info-circle me-2"></i>
                Generate a GitHub Personal Access Token with "repo" scope from
                your GitHub account and paste it here. The token will expire on
                the date selected above.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitHubIntegration;
